import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

export type BadgeTone = 'neutral' | 'accent' | 'success' | 'warning' | 'danger' | 'outline';

const TONE: Record<BadgeTone, string> = {
  neutral: 'bg-white/5 text-zinc-300',
  accent: 'bg-accent/15 text-accent-bright',
  success: 'bg-success/10 text-success',
  warning: 'bg-warning/10 text-warning',
  danger: 'bg-danger/10 text-danger',
  outline: 'border border-dashed border-zinc-600 text-zinc-400',
};

export function Badge({
  tone = 'neutral',
  className,
  children,
}: {
  tone?: BadgeTone;
  className?: string;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        'corner-brand-xs inline-flex items-center gap-1 px-1.5 py-0.5 text-xs leading-4 font-medium whitespace-nowrap',
        TONE[tone],
        className
      )}
    >
      {children}
    </span>
  );
}

const DOT_TONE = {
  neutral: 'bg-zinc-500',
  accent: 'bg-accent-bright',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
} as const;

export function StatusDot({
  tone = 'neutral',
  pulse = false,
  className,
  children,
}: {
  tone?: keyof typeof DOT_TONE;
  pulse?: boolean;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <span className={cn('inline-flex items-center gap-1.5 text-xs text-zinc-300', className)}>
      <span
        aria-hidden
        className={cn('size-1.5 shrink-0 rounded-full', DOT_TONE[tone], pulse && 'animate-pulse')}
      />
      {children}
    </span>
  );
}
